import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

export default class extends Controller {
  static targets = ["content", "editForm", "input", "slideoverLink"]
  static values = { itemId: String }

  connect() {
    this.editing = false
    this.clickTimeout = null
  }

  disconnect() {
    if (this.clickTimeout) clearTimeout(this.clickTimeout)
  }

  handleCardClick(event) {
    // Don't open slideover while editing
    if (this.editing) {
      event.stopPropagation()
      return
    }

    // Don't open slideover when clicking inside the edit form
    if (event.target.closest('[data-roadmap-item-target="editForm"]')) {
      return
    }

    // If clicking on the content area (which supports dblclick-to-edit),
    // delay to differentiate single-click from double-click
    if (event.target.closest('[data-roadmap-item-target="content"]')) {
      if (this.clickTimeout) clearTimeout(this.clickTimeout)
      this.clickTimeout = setTimeout(() => {
        this.openSlideover()
      }, 250)
      return
    }

    // For clicks elsewhere on the card, open slideover immediately
    this.openSlideover()
  }

  openSlideover() {
    if (this.hasSlideoverLinkTarget) {
      this.slideoverLinkTarget.click()
    }
  }

  edit(event) {
    // Cancel any pending click timeout to prevent slideover from opening
    if (this.clickTimeout) clearTimeout(this.clickTimeout)

    event?.preventDefault()
    event?.stopPropagation()

    this.editing = true
    this.contentTarget.classList.add("hidden")
    this.editFormTarget.classList.remove("hidden")
    this.inputTarget.value = this.contentTarget.dataset.rawContent || ""
    this.inputTarget.focus()
  }

  cancel() {
    this.editFormTarget.classList.add("hidden")
    this.contentTarget.classList.remove("hidden")
    this.editing = false
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancel()
    } else if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.save(event)
    }
  }

  async save(event) {
    if (event.type === "blur" || (event.type === "keydown" && event.key === "Enter")) {
      // Proceed with save
    } else {
      return
    }

    const description = this.inputTarget.value.trim()

    try {
      const response = await patch(`/workspace/roadmap_items/${this.itemIdValue}`, {
        body: JSON.stringify({ roadmap_item: { description } }),
        contentType: "application/json",
        responseKind: "json"
      })

      if (response.ok) {
        const data = await response.json
        this.contentTarget.dataset.rawContent = description

        if (description) {
          this.contentTarget.className = "text-sm text-body prose prose-sm dark:prose-invert max-w-none prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-1 prose-headings:font-semibold prose-p:my-1 line-clamp-4"
          this.contentTarget.innerHTML = data.rendered_description || description
        } else {
          this.contentTarget.className = "text-xs text-body-subtle italic cursor-pointer hover:text-heading"
          this.contentTarget.textContent = "Double-click to add description..."
        }

        this.editFormTarget.classList.add("hidden")
        this.contentTarget.classList.remove("hidden")
        this.editing = false
      } else {
        const data = await response.json
        console.error("Failed to update item:", data.error)
      }
    } catch (error) {
      console.error("Error updating item:", error)
    }
  }
}
